import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const RegistrationRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const { currentUser, isAdmin } = useAuth();
  
  // Get API URL from environment variables - use relative path in production
  const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:5001/api');
  
  // Fetch registration requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/auth/registration-requests`);
      setRequests(response.data.requests || []);
    } catch (err) {
      console.error('Error fetching registration requests:', err);
      setError('Failed to load registration requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load requests on component mount
  useEffect(() => {
    if (currentUser && isAdmin()) {
      fetchRequests();
    }
  }, [currentUser]);
  
  // Handle request approval
  const handleApprove = async (requestId) => {
    try {
      setActionInProgress(true);
      
      await axios.post(`${API_URL}/auth/approve-registration`, { requestId });
      
      // Update the local state
      setRequests(requests.filter(req => req.id !== requestId));
      setSuccessMessage('Registration request approved successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error approving registration:', err);
      setError('Failed to approve registration. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Handle request rejection
  const handleReject = async (requestId) => {
    try {
      setActionInProgress(true);
      
      await axios.post(`${API_URL}/auth/reject-registration`, { requestId });
      
      // Update the local state
      setRequests(requests.filter(req => req.id !== requestId));
      setSuccessMessage('Registration request rejected successfully.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error rejecting registration:', err);
      setError('Failed to reject registration. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setActionInProgress(false);
    }
  };
  
  // If not admin, don't show this component
  if (!currentUser || !isAdmin()) {
    return <AccessDenied>You don't have permission to view this page.</AccessDenied>;
  }
  
  return (
    <Container>
      <Title>Registration Requests</Title>
      
      {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {loading ? (
        <LoadingMessage>Loading registration requests...</LoadingMessage>
      ) : requests.length === 0 ? (
        <NoRequestsMessage>No pending registration requests</NoRequestsMessage>
      ) : (
        <RequestsTable>
          <thead>
            <tr>
              <TableHeader>Email</TableHeader>
              <TableHeader>Date Requested</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {requests.map(request => (
              <TableRow key={request.id}>
                <TableCell>{request.email}</TableCell>
                <TableCell>{new Date(request.createdAt).toLocaleString()}</TableCell>
                <TableCell>
                  <ButtonGroup>
                    <ApproveButton 
                      onClick={() => handleApprove(request.id)}
                      disabled={actionInProgress}
                    >
                      Approve
                    </ApproveButton>
                    <RejectButton 
                      onClick={() => handleReject(request.id)}
                      disabled={actionInProgress}
                    >
                      Reject
                    </RejectButton>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </RequestsTable>
      )}
      
      <RefreshButton onClick={fetchRequests} disabled={loading || actionInProgress}>
        Refresh
      </RefreshButton>
    </Container>
  );
};

// Styled components
const Container = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
`;

const RequestsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.medium};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
`;

const TableHeader = styled.th`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: ${props => props.theme.colors.verizonWhite};
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: bold;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: rgba(0, 102, 204, 0.05);
  }
  
  &:hover {
    background-color: rgba(0, 102, 204, 0.1);
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApproveButton = styled(Button)`
  background-color: ${props => props.theme.colors.success};
  color: white;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.successDark};
  }
`;

const RejectButton = styled(Button)`
  background-color: ${props => props.theme.colors.error};
  color: white;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.errorDark};
  }
`;

const RefreshButton = styled(Button)`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: white;
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
`;

const LoadingMessage = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background-color: rgba(0, 102, 204, 0.05);
  border-radius: ${props => props.theme.borderRadius.medium};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const NoRequestsMessage = styled.div`
  padding: ${props => props.theme.spacing.lg};
  background-color: rgba(0, 102, 204, 0.05);
  border-radius: ${props => props.theme.borderRadius.medium};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  background-color: rgba(0, 153, 51, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: rgba(255, 0, 0, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const AccessDenied = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: rgba(255, 0, 0, 0.1);
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.medium};
  text-align: center;
  margin: ${props => props.theme.spacing.lg};
  font-weight: bold;
`;

export default RegistrationRequests;
